import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PublicNav from '../components/PublicNav';
import { Button } from '../components/ui/button';
import { LogIn } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen pb-24 md:pb-0" style={{ backgroundColor: '#F5F0E6' }}>
      <Helmet>
        <title>Privacy Policy | Survivor Fantasy League</title>
        <meta name="description" content="Privacy Policy for Survivor Fantasy League. How we collect, use, and protect your data." />
        <link rel="canonical" href="https://survivorfantasy.live/privacy" />
      </Helmet>

      <PublicNav />

      {/* Content */}
      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#1E3A4C', fontFamily: "'Playfair Display', serif" }}>
            Privacy Policy
          </h1>
          <p className="text-sm mb-8" style={{ color: '#4682B4' }}>
            Effective Date: April 2026
          </p>

          <div className="prose prose-lg max-w-none" style={{ color: '#4682B4', lineHeight: '1.8' }}>
            <p>
              Survivor Fantasy League ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and protect information when you use our website and services at survivorfantasy.live (the "Service").
            </p>

            <h2 className="text-xl font-bold mt-8 mb-4" style={{ color: '#1E3A4C' }}>1. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold mt-6 mb-2" style={{ color: '#1E3A4C' }}>Information You Provide</h3>
            <p>When you create an account or use our Service, we may collect:</p>
            <ul>
              <li>Email address</li>
              <li>Name or display name</li>
              <li>Phone number (optional, for SMS notifications)</li>
              <li>Profile information from Google OAuth (if you sign in with Google)</li>
              <li>League preferences and fantasy team selections</li>
            </ul>

            <h3 className="text-lg font-semibold mt-6 mb-2" style={{ color: '#1E3A4C' }}>Information Collected Automatically</h3>
            <p>When you use our Service, we may automatically collect:</p>
            <ul>
              <li>Device information (browser type, operating system)</li>
              <li>Usage data (pages visited, features used)</li>
              <li>IP address</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h2 className="text-xl font-bold mt-8 mb-4" style={{ color: '#1E3A4C' }}>2. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide and maintain the Service</li>
              <li>Create and manage your account</li>
              <li>Send you notifications about your leagues, drafts, and scores</li>
              <li>Send email or SMS notifications (with your consent)</li>
              <li>Respond to your questions and support requests</li>
              <li>Improve and develop new features</li>
              <li>Protect against fraud and abuse</li>
            </ul>

            <h2 className="text-xl font-bold mt-8 mb-4" style={{ color: '#1E3A4C' }}>3. Third-Party Services</h2>
            <p>We use the following third-party services to operate our platform:</p>
            <ul>
              <li><strong>Google OAuth:</strong> For secure authentication when you sign in with Google</li>
              <li><strong>SendGrid:</strong> For sending email notifications</li>
              <li><strong>Twilio:</strong> For sending SMS notifications (if enabled)</li>
              <li><strong>Google AdSense:</strong> For displaying advertisements</li>
            </ul>
            <p>
              These services have their own privacy policies governing how they handle your data. We encourage you to review their policies.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-4" style={{ color: '#1E3A4C' }}>4. Data Sharing</h2>
            <p>
              <strong>We do not sell your personal information to advertisers or third parties.</strong>
            </p>
            <p>We may share your information only in the following circumstances:</p>
            <ul>
              <li>With other players in your league (display name, scores, and league activity)</li>
              <li>With service providers who assist in operating our Service</li>
              <li>When required by law or to protect our rights</li>
              <li>In connection with a merger or acquisition (with notice to you)</li>
            </ul>

            <h2 className="text-xl font-bold mt-8 mb-4" style={{ color: '#1E3A4C' }}>5. Data Security</h2>
            <p>
              We implement reasonable security measures to protect your information. However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your data.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-4" style={{ color: '#1E3A4C' }}>6. Your Choices</h2>
            <p>You can:</p>
            <ul>
              <li>Update your account information at any time</li>
              <li>Opt out of email or SMS notifications in your settings</li>
              <li>Request deletion of your account by contacting us</li>
              <li>Use browser settings to manage cookies</li>
            </ul>

            <h2 className="text-xl font-bold mt-8 mb-4" style={{ color: '#1E3A4C' }}>7. Children's Privacy</h2>
            <p>
              Our Service is not intended for children under 13. We do not knowingly collect information from children under 13. If you believe we have collected such information, please contact us.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-4" style={{ color: '#1E3A4C' }}>8. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the effective date.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-4" style={{ color: '#1E3A4C' }}>9. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy, please contact us at:
            </p>
            <p>
              <a href="mailto:support@survivorfantasy.live" className="text-[#FF7F50] underline">support@survivorfantasy.live</a>
            </p>

            <h2 className="text-xl font-bold mt-8 mb-4" style={{ color: '#1E3A4C' }}>10. Disclaimer</h2>
            <p>
              Survivor Fantasy League is not affiliated with, endorsed by, or sponsored by CBS, Survivor Productions LLC, or any related entities. "Survivor" is a registered trademark of CBS.
            </p>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="py-12" style={{ backgroundColor: '#1E3A4C' }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo-new.png" alt="Survivor Fantasy League" className="w-10 h-10" />
              <span className="text-white font-bold text-lg">Survivor Fantasy League</span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm" style={{ color: '#F5F0E6' }}>
              <Link to="/" className="hover:text-[#FF7F50] transition-colors">Home</Link>
              <Link to="/rules" className="hover:text-[#FF7F50] transition-colors">Rules</Link>
              <Link to="/faq" className="hover:text-[#FF7F50] transition-colors">FAQ</Link>
              <Link to="/about" className="hover:text-[#FF7F50] transition-colors">About</Link>
              <Link to="/blog" className="hover:text-[#FF7F50] transition-colors">Blog</Link>
              <Link to="/privacy" className="hover:text-[#FF7F50] transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-[#FF7F50] transition-colors">Terms</Link>
            </div>
          </div>
          
          <div className="border-t mt-8 pt-8 text-center text-sm" style={{ borderColor: '#4682B4', color: '#4682B4' }}>
            <p>© {new Date().getFullYear()} Survivor Fantasy League. Not affiliated with CBS or Survivor.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
